'use client'

import AirspaceAlertWrapper from '@/src/components/common/home/common/airspaceAlert'
import ErrorModal from '@/src/components/common/itinerary/errorModal/errorModal'
import LoaderImage from '@/src/components/common/loaderImage'
import EmptyFlightScreenWrapper from '@/src/components/common/srp/emptyFlightScreen'
import SrpErrorStateWrapper from '@/src/components/common/srp/errorState'
import AirlineFilterWrapperTopBar from '@/src/components/common/srp/filters/airlineFilterTopBar'
import NearbyAirportPopup from '@/src/components/common/srp/nearbyAirportPopup'
import StillAroundPopup from '@/src/components/common/srp/stillAroundPopup/StillAroundPopup'
import Statusbar from '@/src/components/common/ui/statusbar'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import CouponSectionWrapper from '@/src/components/desktop/srp/couponSection'
import FareFamilyWrapper from '@/src/components/desktop/srp/fareFamily'
import MulticityRoundTripDialog from '@/src/components/desktop/srp/multicityRoundTripDialog'
import FullPageLoader from '@/src/components/mobile/fullPageLoader'
import MobileDateCarouselWrapper from '@/src/components/mobile/srp/dateCarousel/MobileDateCarouselWrapper'
import FlightDetailsWrapper from '@/src/components/mobile/srp/flightDetails'
import FlightCardShimmer from '@/src/components/mobile/srp/loadingShimmers/flightCardShimmer'
import SrpLoadingShimmer from '@/src/components/mobile/srp/loadingShimmers/srpLoadingShimmer'
import SrpFilterAndSortTabsAndDrawerWrapper from '@/src/components/mobile/srp/srpFilterAndSortTabsBar/srpFilterAndSortTabsAndDrawerWrapper'
import SrpHeaderWithSearchWrapper from '@/src/components/mobile/srp/srpSearchModifier'
import { MOBILE } from '@/src/constants/deviceTypes'
import { TravelClassType } from '@/src/constants/travelers'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useAIFeatureEnableManager } from '@/src/hooks/AIFeatureEnableManager'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useNearByAirportAlertManager from '@/src/hooks/srp/nearbyAirportAlertManager'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSrpController from '@/src/hooks/srp/srpController'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { selectSRPFloatingAIChatEnabled } from '@/src/redux/slices/aiFeatureSlice'
import { useAppSelector } from '@/src/redux/store'
import { validSearchQueryDateFormats } from '@/src/utils/date'
import { onPageStart } from '@/src/utils/generalUtils/onPageStart'
import { useSetLanguageCookie } from '@/src/utils/language'
import { parseMulticitySectors } from '@/src/utils/srp/searchUtils'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'

const HEADER_DATE_FORMAT = 'D MMM'
const DEFAULT_DATE_INPUT_FORMATS = [...validSearchQueryDateFormats, 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD']

const formatHeaderDate = (dateValue?: string): string => {
    if (!dateValue) return ''

    for (const inputFormat of DEFAULT_DATE_INPUT_FORMATS) {
        const parsed = dayjs(dateValue, inputFormat)
        if (parsed.isValid()) {
            return parsed.format(HEADER_DATE_FORMAT)
        }
    }

    const fallbackParsed = dayjs(dateValue)
    return fallbackParsed.isValid() ? fallbackParsed.format(HEADER_DATE_FORMAT) : ''
}
// Lazy-load the AI chat — heavy dependency tree, and only renders behind a feature flag.
const SRPFloatingAIChat = dynamic(() => import('@/src/components/common/srp/aiFilter/SRPFloatingAIChat'), {
    ssr: false
})

// Shown only if a view's JS chunk is still downloading when results arrive. Usually
// never visible — the chunk fetch overlaps the in-flight search API call.
const ViewChunkFallback = () => (
    <Box sx={{ mt: 2, px: 2 }}>
        {[...Array(7)].map((_, i) => (
            <FlightCardShimmer key={i} />
        ))}
    </Box>
)

// Exactly one SRP view renders per search (the mode is known synchronously from the URL),
// so code-split each view root: a one-way search no longer ships the RT-solution/RT-split
// trees, and vice versa. Keeps SSR on — during SSR results are still loading, so the
// shimmer (not these views) is what renders.
const RTSolutionFlowWrapperMobile = dynamic(() => import('@/src/components/mobile/srp/rtSolutionViewFlow'), {
    loading: ViewChunkFallback
})
const RTSplitViewWrapperMobile = dynamic(() => import('@/src/components/mobile/srp/rtSplitView'), {
    loading: ViewChunkFallback
})
const FlightResultsWrapper = dynamic(() => import('@/src/components/mobile/srp/flightResults'), {
    loading: ViewChunkFallback
})

const SrpContainerMobile = () => {
    // Set selectedLanguage cookie on page load based on locale
    useSetLanguageCookie()

    useEffect(() => {
        onPageStart('FLIGHTS_SRP')
    }, [])

    const t = useTranslations()
    const locale = useLocale()

    const {
        handleCreateBooking,
        handleFlightDetailsClose,
        onViewFlightDetails,
        selectedFlightForFlightDetails,
        isCreateItineraryLoading,
        errorCreateItinerary,
        setErrorCreateItinerary,
        handleRetryCreateBooking,
        selectedFlightForFareFamily,
        handleBookClick,
        rawFareFamilyData,
        isBookingInProgress,
        handleCloseFareFamily,
        showStillAroundPopup,
        handleReFetchWithSameQuery,
    } = useSrpController({ deviceType: MOBILE })
    const srpFloatingAIChatEnabled = useAppSelector(selectSRPFloatingAIChatEnabled)
    const { srpSmartFilterSort } = useAIFeatureEnableManager()
    const {
        srpResultsLoading,
        srpResultsError,
        isMulticity,
        invalidUrl,
        showEmptyFlightScreen,
        jsons,
        sectors,
        sid,
        isRT,
        srpViewMode,
        selectedJourneyLeg
    } = useSearchResults()

    // Nearby airport alert manager - handles all nearby airport logic internally
    const {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen,
        popupData
    } = useNearByAirportAlertManager({
        onContinue: handleBookClick,
        jsons: jsons || null,
        sectors: sectors || null,
        handleFlightDetailsClose
    })

    const { triggerSrpEvent } = useClevertapForSrp()

    // Round-trip-like multicity (J1 A->B, J2 B->A) is flagged by useGetSearchQueryURL as
    // invalidUrl='roundTripLikeMulticity'. Show the conversion dialog instead of the error screen,
    // and track it once per detection via Air_UI_Action (NewRelic + CleverTap).
    const [roundTripDialogOpen, setRoundTripDialogOpen] = useState(false)
    const openSearchModifierRef = useRef<() => void>(() => { })

    useEffect(() => {
        const isRoundTripLikeMulticity = invalidUrl === 'roundTripLikeMulticity'
        setRoundTripDialogOpen(isRoundTripLikeMulticity)
        if (isRoundTripLikeMulticity) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'round_trip_like_multicity',
                action_type: 'VALIDATION'
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidUrl])

    const { urlQueryObj } = useGetSearchParamsObj()

    const {
        from,
        to,
        class: travelClass,
        adults,
        childs,
        infants,
        depart_date = '',
        return_date = ''
    } = urlQueryObj || {}
    const multicitySegments = isMulticity ? parseMulticitySectors(urlQueryObj).sectors : []
    const multicityRouteSegments = isMulticity
        ? multicitySegments.filter(segment => segment.from || segment.to)
        : undefined

    const formattedDepartDate = isMulticity
        ? formatHeaderDate(multicitySegments[0]?.depart_date)
        : formatHeaderDate(depart_date)

    const formattedReturnDate = isMulticity
        ? (multicitySegments.length > 1
            ? formatHeaderDate(multicitySegments[multicitySegments.length - 1]?.depart_date)
            : '')
        : formatHeaderDate(return_date)
    const totalTravellers = Number(adults || '1') + Number(childs || '0') + Number(infants || '0')
    const travelers = `${totalTravellers} ${t('common.traveller', { s: totalTravellers > 1 ? 's' : '' })}`

    return (
        <Box
            sx={{
                bgcolor: '#FFFFFF',
                height: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <LoaderImage height={50} width={360} />
            <Toaster position="bottom-center" reverseOrder={false} gutter={8} />
            <ErrorBoundaryWrapper identifier="srp-header-with-search-wrapper-PWA">
                <SrpHeaderWithSearchWrapper
                    jsons={jsons}
                    from={from as string}
                    to={to as string}
                    routeSegments={multicityRouteSegments}
                    departDate={formattedDepartDate}
                    returnDate={formattedReturnDate}
                    travelers={travelers}
                    travelClass={(travelClass || 'Economy') as TravelClassType}
                    deviceType={MOBILE}
                    srpResultsLoading={srpResultsLoading}
                    onRegisterOpenSearch={fn => {
                        openSearchModifierRef.current = fn
                    }}
                />
            </ErrorBoundaryWrapper>

            {/* Sort Tabs Wrapper */}

            <Statusbar loading={srpResultsLoading} />
            {!!srpResultsError || (invalidUrl && invalidUrl !== 'roundTripLikeMulticity') ? (
                <ErrorBoundaryWrapper identifier="srp-error-state-wrapper-PWA">
                    <SrpErrorStateWrapper
                        pastDateError={invalidUrl === 'dateInPast'}
                        errorStatus={invalidUrl === 'requiredFieldsMissing' ? 400 : srpResultsError?.status || 500}
                    />
                </ErrorBoundaryWrapper>
            ) : invalidUrl === 'roundTripLikeMulticity' ? null : srpResultsLoading ? (
                <ErrorBoundaryWrapper identifier="srp-loading-shimmer-PWA">
                    <SrpLoadingShimmer />
                </ErrorBoundaryWrapper>
            ) : (
                <>
                    {srpViewMode === 'rtSolution' ? (
                        <ErrorBoundaryWrapper identifier="rt-solution-flow-wrapper-mobile-PWA">
                            <RTSolutionFlowWrapperMobile handleFlightBook={handleCreateBooking} />
                        </ErrorBoundaryWrapper>
                    ) : srpViewMode === 'rtSplit' ? (
                        <ErrorBoundaryWrapper identifier="rt-split-view-wrapper-mobile-PWA">
                            <RTSplitViewWrapperMobile
                                handleFlightBook={handleCreateBooking}
                                isBookingInProgress={isBookingInProgress}
                            />
                        </ErrorBoundaryWrapper>
                    ) : (
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            <AirspaceAlertWrapper />
                            <Box sx={{ display: 'flex', flexDirection: 'column', px: 2 }}>
                                <CouponSectionWrapper jsons={jsons} isRT={isRT} deviceType={MOBILE} />
                                {!isMulticity && <ErrorBoundaryWrapper identifier="mobile-date-carousel-wrapper-PWA">
                                    <MobileDateCarouselWrapper />
                                </ErrorBoundaryWrapper>}
                                <ErrorBoundaryWrapper identifier="airline-filter-wrapper-top-bar-PWA">
                                    <AirlineFilterWrapperTopBar showArrows={false} deviceType={MOBILE} />
                                </ErrorBoundaryWrapper>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: '#F9FAFB' }}>
                                <ErrorBoundaryWrapper identifier="srp-filter-and-sort-tabs-and-drawer-wrapper-PWA">
                                    <SrpFilterAndSortTabsAndDrawerWrapper showEmptyFlightScreen={showEmptyFlightScreen} />
                                </ErrorBoundaryWrapper>
                                {showEmptyFlightScreen ? (
                                    <ErrorBoundaryWrapper identifier="empty-flight-screen-wrapper-PWA">
                                        <EmptyFlightScreenWrapper deviceType={MOBILE} />
                                    </ErrorBoundaryWrapper>
                                ) : (
                                    <ErrorBoundaryWrapper identifier="OW-flight-results-wrapper-PWA">
                                        <FlightResultsWrapper
                                            onBookFlight={handleFlightSelection} // Use handleFlightSelection instead of handleBookClick
                                            onViewDetails={flight => onViewFlightDetails([flight])}
                                            onSortByPrice={() => { }}
                                        />
                                    </ErrorBoundaryWrapper>
                                )}
                            </Box>
                        </Box>
                    )}

                    <ErrorBoundaryWrapper identifier="srp-floating-ai-chat-PWA">

                        {srpSmartFilterSort && srpFloatingAIChatEnabled && <SRPFloatingAIChat
                            key={`srp-floating-ai-chat-${selectedJourneyLeg}`} />}

                    </ErrorBoundaryWrapper>

                    {(isCreateItineraryLoading || isBookingInProgress) && (
                        <FullPageLoader
                            message={
                                <Box mt={3} textAlign="center">
                                    <Typography variant="h6" fontWeight={700} color="text.secondary">
                                        {isBookingInProgress && !isCreateItineraryLoading
                                            ? t('createItineraryStates.loadingFareOptions')
                                            : t('createItineraryStates.creatingItinerary')}
                                    </Typography>
                                </Box>
                            }
                        />
                    )}

                    <SwipeableDrawerWithBackNavigation
                        open={!!selectedFlightForFlightDetails}
                        onClose={handleFlightDetailsClose}
                        anchor="bottom"
                    >
                        {selectedFlightForFlightDetails ? (
                            <ErrorBoundaryWrapper identifier="flight-details-wrapper-PWA">
                                <FlightDetailsWrapper
                                    flights={selectedFlightForFlightDetails}
                                    sectors={sectors!}
                                    jsons={jsons!}
                                    onSelect={() => handleFlightSelection(selectedFlightForFlightDetails[0])} // Use handleFlightSelection here too
                                    sid={sid!}
                                    showBaggage={true}
                                    isRtSolutionPair={false}
                                />
                            </ErrorBoundaryWrapper>
                        ) : null}
                    </SwipeableDrawerWithBackNavigation>

                    <NearbyAirportPopup
                        open={isOpen}
                        onClose={handleNearbyPopupClose}
                        onContinue={handleNearbyPopupContinue}
                        data={popupData}
                    />

                    {errorCreateItinerary && (
                        <ErrorModal
                            isOpen={true}
                            primaryError={t('createItineraryStates.serverError')}
                            secondaryError={t('createItineraryStates.errorMessage')}
                            primaryButtonText={t('common.retry')}
                            secondaryButtonText={t('common.close')}
                            primaryButtonAction={handleRetryCreateBooking}
                            secondaryButtonAction={() => setErrorCreateItinerary?.(null)}
                        />
                    )}
                    <SwipeableDrawerWithBackNavigation
                        open={!!selectedFlightForFareFamily}
                        onClose={handleCloseFareFamily}
                        anchor="bottom"
                        keepMounted
                    >
                        <ErrorBoundaryWrapper identifier="fare-family-wrapper-PWA">
                            <FareFamilyWrapper
                                flight={selectedFlightForFareFamily ?? undefined}
                                sectors={sectors!}
                                handleClose={handleCloseFareFamily}
                                handleCreateBooking={(flight, plan) => handleCreateBooking([flight], [plan])}
                                jsons={jsons!}
                                desktop={false}
                                rawFareFamilyData={rawFareFamilyData!}
                            />
                        </ErrorBoundaryWrapper>
                    </SwipeableDrawerWithBackNavigation>
                </>
            )}


            {showStillAroundPopup && <StillAroundPopup handleReFetchWithSameQuery={handleReFetchWithSameQuery} />}

            <MulticityRoundTripDialog
                open={roundTripDialogOpen}
                onClose={() => setRoundTripDialogOpen(false)}
                onEdit={() => openSearchModifierRef.current()}
            />
        </Box>
    )
}

export default SrpContainerMobile
